import { Container, LoadingScreen } from '@/shared/components'
import MissingResourcesAlert from '@/shared/components/MissingResourcesAlert'
import { useGetAllInstallationsForPartnership } from '@/features/suppliers/hooks/installations'
import { useGetAllPartnerships } from '@/features/suppliers/hooks/partnerships'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import {
  BreadcrumbsBar,
  Button,
  DataBlock,
  H2,
  Table,
  TableColumnHeader,
  TableRow,
  PageSection
} from '@/common'
import useDialog from '@/common/hooks/useDialog'
import { compact } from 'es-toolkit'
import { useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import InstallationEditDialog from './dialogs/InstallationEditDialog'
import EmissionsReportCreateDialog from './dialogs/SupplierEmissionsReportCreateDialog'
import SupplierEmissionsReportEditDialog from './dialogs/SupplierEmissionsReportEditDialog'
import {
  useGetAllSupplierEmissionsReportsForInstallation,
  SupplierEmissionsReport
} from '@/features/suppliers/hooks/supplierEmissionsReport'
import SupplierEmissionsReportRow from './components/SupplierEmissionsReportRow'
import DeleteSupplierEmissionsReportDialog from './dialogs/DeleteSupplierEmissionsReportDialog'

export default function InstallationDetailsPage() {
  const { t } = useI18n()
  const {
    open: isEditInstallationDialogOpen,
    openDialog: openEditInstallationDialog,
    closeDialog: closeEditInstallationDialog
  } = useDialog()
  const {
    open: isEmissionsReportCreateDialogOpen,
    openDialog: openEmissionsReportCreateDialog,
    closeDialog: closeEmissionsReportCreateDialog
  } = useDialog()
  const {
    open: isDeleteSupplierEmissionsReportDialogOpen,
    openDialog: openDeleteSupplierEmissionsReportDialog,
    closeDialog: closeDeleteSupplierEmissionsReportDialog
  } = useDialog()

  const [isEmissionsReportEditDialogOpen, setIsEmissionsReportEditDialogOpen] = useState(false)
  const [reportToEdit, setReportToEdit] = useState<SupplierEmissionsReport | null>(null)
  const [reportToDelete, setReportToDelete] = useState<SupplierEmissionsReport | null>(null)

  const organizationId = useSession().session.organization.id
  const partnershipId = useParams().partnershipId!
  const installationId = useParams().installationId!

  const partnershipsQuery = useGetAllPartnerships(organizationId)
  const installationsQuery = useGetAllInstallationsForPartnership(partnershipId)
  const emissionsReportsQuery = useGetAllSupplierEmissionsReportsForInstallation(
    partnershipId,
    installationId
  )

  const arePartnershipsLoading = partnershipsQuery.isLoading
  const areInstallationsLoading = installationsQuery.isLoading
  const areEmissionsReportsLoading = emissionsReportsQuery.isLoading

  const partnerships = partnershipsQuery.data ?? []
  const installations = installationsQuery.data ?? []
  const emissionsReports = emissionsReportsQuery.data ?? []

  const partnership = partnerships.find(partnership => partnership.id === partnershipId)
  const installation = installations.find(installation => installation.id === installationId)

  const hasEmissionReports = emissionsReports.length > 0

  const installationEconomicActivity = useMemo(
    () => t(`common:goodsFamily.${installation?.goodsFamily}`),
    [installation, t]
  )

  const installationAddress = useMemo(() => {
    const streetAndNumber =
      installation?.street || installation?.streetNumber
        ? compact([installation?.street, installation?.streetNumber]).join(' ')
        : ''

    return compact([
      streetAndNumber,
      installation?.postCode,
      installation?.city,
      t(`common:countriesByIsoAlpha2.${installation?.country}`)
    ]).join(', ')
  }, [installation, t])

  const handleEditReport = (report: SupplierEmissionsReport) => {
    setReportToEdit(report)
    setIsEmissionsReportEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setIsEmissionsReportEditDialogOpen(false)
    setReportToEdit(null)
  }

  if (arePartnershipsLoading || areInstallationsLoading || areEmissionsReportsLoading) {
    return <LoadingScreen />
  }

  if (!partnership || !partnershipId || !installation) {
    return <Navigate to="/suppliers" />
  }

  const breadcrumbs = [
    { label: t('common:breadcrumbs.suppliers'), to: '/suppliers' },
    { label: partnership?.name || '', to: '/suppliers/' + partnershipId },
    { label: installation?.name || '', to: '' }
  ]

  return (
    <>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header className="flex justify-between items-center">
          <H2>{installation?.name}</H2>
        </header>
        <PageSection
          title={t('installations:installationViewPage.installationDetails.title')}
          actions={
            <Button variant="txt-default" onClick={openEditInstallationDialog}>
              {t('installations:installationViewPage.installationDetails.actions.edit')}
            </Button>
          }
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <DataBlock
              label={t(
                'installations:installationViewPage.installationDetails.dataSections.economicActivity'
              )}
              data={installationEconomicActivity}
            />
            <DataBlock
              label={t(
                'installations:installationViewPage.installationDetails.dataSections.address'
              )}
              data={installationAddress}
            />
          </div>
        </PageSection>
        <PageSection
          title={t('supplierEmissionsReport:emissionReports.title')}
          actions={
            hasEmissionReports && (
              <Button onClick={openEmissionsReportCreateDialog}>
                {t('supplierEmissionsReport:emissionReports.actions.add')}
              </Button>
            )
          }
        >
          {!hasEmissionReports ? (
            <MissingResourcesAlert
              title={t('supplierEmissionsReport:emissionReports.missingEmissionReports.title')}
              description={t(
                'supplierEmissionsReport:emissionReports.missingEmissionReports.description'
              )}
              action={{
                label: t('supplierEmissionsReport:emissionReports.missingEmissionReports.action'),
                onClick: openEmissionsReportCreateDialog
              }}
            />
          ) : (
            <Table>
              <thead>
                <TableRow>
                  <TableColumnHeader>
                    {t('supplierEmissionsReport:emissionReports.table.headers.goodsDescription')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('supplierEmissionsReport:emissionReports.table.headers.emissionReport')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('supplierEmissionsReport:emissionReports.table.headers.cnCode')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('supplierEmissionsReport:emissionReports.table.headers.period')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('supplierEmissionsReport:emissionReports.table.headers.createdOn')}
                  </TableColumnHeader>
                  <TableColumnHeader />
                </TableRow>
              </thead>
              <tbody>
                {emissionsReports.map(report => (
                  <SupplierEmissionsReportRow
                    key={report.id}
                    report={report}
                    basisForReportingPeriod={installation.basisForReportingPeriod}
                    onEdit={handleEditReport}
                    onDeleteClick={() => {
                      setReportToDelete(report)
                      openDeleteSupplierEmissionsReportDialog()
                    }}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </PageSection>
      </Container>
      <InstallationEditDialog
        isOpen={isEditInstallationDialogOpen}
        close={closeEditInstallationDialog}
        installation={installation}
        partnershipId={partnershipId}
      />
      <EmissionsReportCreateDialog
        isOpen={isEmissionsReportCreateDialogOpen}
        close={closeEmissionsReportCreateDialog}
        installation={installation}
        partnershipId={partnershipId}
        supplierEmissionsReports={emissionsReports}
      />
      {reportToEdit && (
        <SupplierEmissionsReportEditDialog
          isOpen={isEmissionsReportEditDialogOpen}
          close={handleCloseEditDialog}
          partnershipId={partnershipId}
          installation={installation}
          supplierEmissionsReports={emissionsReports}
          reportToEdit={reportToEdit}
        />
      )}
      {reportToDelete && (
        <DeleteSupplierEmissionsReportDialog
          isOpen={isDeleteSupplierEmissionsReportDialogOpen}
          close={closeDeleteSupplierEmissionsReportDialog}
          reportName={reportToDelete.goods[0].goodsDescription}
          partnershipId={partnershipId}
          installationId={installationId}
          supplierEmissionsReportId={reportToDelete.id}
          onAfterLeave={() => setReportToDelete(null)}
        />
      )}
    </>
  )
}
